// Thin wrappers over the generated tfx orval hooks (pattern: features/effect-packs/api.ts).
// Unwrapping happens via `selectData` per the envelope rule; every mutation invalidates the
// affected list(s) so the pages refetch.
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { customInstance } from '@/api/http';
import { selectData } from '@/api/envelope';
import type { TfxRenderJobDTO } from '@/api/generated/model';
import {
  useListTfxDrafts,
  useSubmitTfxDraft as useSubmitTfxDraftMutation,
  useApproveTfxDraft as useApproveTfxDraftMutation,
  useRejectTfxDraft as useRejectTfxDraftMutation,
  getListTfxDraftsQueryKey,
} from '@/api/generated/tfx-drafts/tfx-drafts';
import { getListTfxPublishedQueryKey } from '@/api/generated/tfx-published/tfx-published';
import { MEDIA_PACKAGES_QUERY_KEY } from '@/features/media/api';
import {
  useListTfxRenderJobs,
  useCreateTfxRenderJob as useCreateTfxRenderJobMutation,
  createTfxPromptRefUploadUrl,
  getListTfxRenderJobsQueryKey,
} from '@/api/generated/tfx-render-jobs/tfx-render-jobs';

/** Bare-URL key (no params) — prefix-matches every drafts-list variant (mine + review queue). */
export const TFX_DRAFTS_QUERY_KEY = getListTfxDraftsQueryKey();

/** Stable key for the published-rows list (the media library shows the CDN catalog). */
export const TFX_PUBLISHED_QUERY_KEY = getListTfxPublishedQueryKey();

/** Bare-URL key — prefix-matches every render-jobs list variant. */
export const TFX_RENDER_JOBS_QUERY_KEY = getListTfxRenderJobsQueryKey();

/** My drafts (GET /admin/api/tfx/drafts?mine=true), envelope-unwrapped. */
export function useMyTfxDrafts() {
  return useListTfxDrafts({ mine: true }, { query: { select: selectData } });
}

/** Review queue: everyone's SUBMITTED drafts (mine=false&status=SUBMITTED), envelope-unwrapped. */
export function useTfxReviewQueue() {
  return useListTfxDrafts({ mine: false, status: 'SUBMITTED' }, { query: { select: selectData } });
}

/** Submit for review: DRAFT -> SUBMITTED, then refetch the drafts lists. */
export function useSubmitTfxDraft() {
  const qc = useQueryClient();
  return useSubmitTfxDraftMutation({
    mutation: {
      onSuccess: () => void qc.invalidateQueries({ queryKey: TFX_DRAFTS_QUERY_KEY }),
    },
  });
}

/** Approve: SUBMITTED -> APPROVED; publish + catalog rebuild run backend-side, so both
 *  the published rows AND the media library's CDN view are invalidated with the drafts. */
export function useApproveTfxDraft() {
  const qc = useQueryClient();
  return useApproveTfxDraftMutation({
    mutation: {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: TFX_DRAFTS_QUERY_KEY });
        void qc.invalidateQueries({ queryKey: TFX_PUBLISHED_QUERY_KEY });
        void qc.invalidateQueries({ queryKey: MEDIA_PACKAGES_QUERY_KEY });
      },
    },
  });
}

/** Reject: SUBMITTED -> REJECTED with a required reason, then refetch the drafts lists. */
export function useRejectTfxDraft() {
  const qc = useQueryClient();
  return useRejectTfxDraftMutation({
    mutation: {
      onSuccess: () => void qc.invalidateQueries({ queryKey: TFX_DRAFTS_QUERY_KEY }),
    },
  });
}

// Delist/relist now live in features/media: they operate on the CDN catalog
// (which includes the factory-published packages this endpoint never saw), not
// on the tfx_published rows alone.

/** My recent jobs (Create tab): PROMPT generations AND SPEC param-edit
 *  re-renders, newest first; polls while any job is still active. */
export function useMyTfxJobs() {
  return useListTfxRenderJobs(
    { limit: 20 },
    {
      query: {
        select: selectData,
        // refetchInterval sees the RAW query data (select applies per-observer),
        // so unwrap the envelope here by hand.
        refetchInterval: (query) => {
          const jobs = query.state.data?.data;
          const active = jobs?.some((j) => j.status === 'QUEUED' || j.status === 'RUNNING');
          return active ? 4000 : false;
        },
      },
    },
  );
}

/** Queue a render job (Create tab uses kind PROMPT), then refetch the jobs list. */
export function useCreatePromptJob() {
  const qc = useQueryClient();
  return useCreateTfxRenderJobMutation({
    mutation: {
      onSuccess: () => void qc.invalidateQueries({ queryKey: TFX_RENDER_JOBS_QUERY_KEY }),
    },
  });
}

// Backend PROMPT_REF_FILENAME allows only these canonical extensions; browsers
// hand out plenty of aliases for the same MIME types (Windows JPEG clipboard
// saves as .jfif, Chromium's video/mp4 filter admits .m4v, ...).
const EXT_BY_MIME: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
};
const EXT_ALIASES: Record<string, string> = {
  jpeg: 'jpg',
  jfif: 'jpg',
  pjpeg: 'jpg',
  pjp: 'jpg',
  jpe: 'jpg',
  m4v: 'mp4',
  qt: 'mov',
};
const ALLOWED_EXTS = new Set(['png', 'jpg', 'webp', 'mp4', 'mov']);

/** The backend's PROMPT_REF_FILENAME charset is strict (the name is echoed into
 *  an S3 key, ".." forbidden) — normalise whatever the browser hands us to fit
 *  it, deriving the extension from the MIME type when the name's own extension
 *  is missing or an alias. Throws for genuinely unsupported files so callers
 *  can fail BEFORE any upload starts. */
export function sanitizeRefName(file: File): string {
  const name = file.name;
  const dot = name.lastIndexOf('.');
  const rawExt = dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
  let ext = EXT_ALIASES[rawExt] ?? rawExt;
  if (!ALLOWED_EXTS.has(ext)) ext = EXT_BY_MIME[file.type] ?? '';
  if (!ext) throw new Error(`Unsupported reference file type: ${name || file.type || 'unknown'}`);
  const base = (dot > 0 ? name.slice(0, dot) : name)
    .replace(/[^A-Za-z0-9._-]/g, '-')
    .replace(/\.{2,}/g, '.')
    .replace(/^[^A-Za-z0-9]+/, '')
    .replace(/[.-]+$/, '')
    .slice(0, 100);
  return `${base || 'ref'}.${ext}`;
}

// ---- Generated-spec shapes (mirror the worker's check_spec contract) ----

export interface TfxSpecParam {
  key: string;
  type?: string;
  default?: number;
  min?: number;
  max?: number;
}

export interface TfxSpec {
  id: string;
  version?: string;
  kind?: string;
  kernelId?: string;
  params?: TfxSpecParam[];
  [extra: string]: unknown;
}

/** Backend tfx_render_job.options_json budget is 2048; keep a safety margin. */
export const MAX_JOB_OPTIONS_LEN = 2000;

/** The spec a job was rendered from: SPEC jobs carry it verbatim in `source`;
 *  PROMPT jobs expose it as the presigned `spec` artifact (the bucket's CORS
 *  allows browser GETs). BARE axios for the presigned URL — see uploadPromptRef. */
export async function loadJobSpec(job: TfxRenderJobDTO): Promise<TfxSpec> {
  if (job.kind === 'SPEC' && job.source) return JSON.parse(job.source) as TfxSpec;
  const url = job.artifacts?.find((a) => a.kind === 'spec')?.url;
  if (!url) throw new Error('This job has no spec to start from');
  const res = await axios.get<TfxSpec | string>(url);
  return typeof res.data === 'string' ? (JSON.parse(res.data) as TfxSpec) : res.data;
}

/** S2 sidecar rail — presign + PUT + attach one draft side-asset (footage
 *  clip / texture plate / slot image). Hand-written against customInstance
 *  (not orval-regenerated) per the openapi-drift rule; the S3 PUT uses BARE
 *  axios like uploadPromptRef. The file name must match what the manifest
 *  references — the approve gate cross-checks by name. */
export async function uploadDraftAsset(draftId: number, file: File): Promise<string> {
  const presign = await customInstance<{ data?: { url?: string; s3Key?: string } }>({
    url: `/admin/api/tfx/drafts/${draftId}/asset-upload-url`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: { fileName: file.name },
  });
  const target = presign.data;
  if (!target?.url || !target.s3Key) {
    throw new Error('Asset upload-URL request returned no target');
  }
  await axios.put(target.url, file, {
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  });
  await customInstance<unknown>({
    url: `/admin/api/tfx/drafts/${draftId}/assets`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: { s3Key: target.s3Key },
  });
  return file.name;
}

/** Presign + PUT one PROMPT reference asset; returns its s3Key. The PUT uses
 *  BARE axios — the authed instance would attach a bearer token and baseURL,
 *  both of which break a presigned S3 URL. */
export async function uploadPromptRef(file: File, filename: string): Promise<string> {
  const res = await createTfxPromptRefUploadUrl({
    filename,
    contentType: file.type || 'application/octet-stream',
  });
  const target = selectData(res);
  if (!target?.url || !target.s3Key) {
    throw new Error('Reference upload-URL request returned no target');
  }
  await axios.put(target.url, file, {
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  });
  return target.s3Key;
}
